// Client-side interaction tracking for the platform and the public blade tools.
//
// Privacy: network tracking is OFF until the visitor grants consent
// (hasTrackingConsent()). Without consent every track() call is a no-op — this is
// the ePrivacy-safe default for an EU compliance-grade brand. PII (email) is only
// ever attached to a consented lead_capture. The send is fire-and-forget.

import { isSupabaseConfigured } from '@/lib/supabase'
import { isDemo } from '@/lib/demoData'
import { getCookieConsent } from '@/components/CookieConsent/CookieConsent'
import type {
  CrmInteractionEventInput,
  CrmInteractionEventType,
  CrmInteractionSurface,
} from '@beetlesense/shared'

const ANON_KEY = 'bs_anon_id'
const SESSION_KEY = 'bs_session_id'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? ''
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''

function hasWindow(): boolean {
  return typeof window !== 'undefined'
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function getAnonId(): string {
  if (!hasWindow()) return 'ssr'
  let id = window.localStorage.getItem(ANON_KEY)
  if (!id) {
    id = randomId()
    window.localStorage.setItem(ANON_KEY, id)
  }
  return id
}

function getSessionId(): string {
  if (!hasWindow()) return 'ssr'
  let id = window.sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = randomId()
    window.sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

/**
 * Interaction tracking is analytics — it rides the platform's existing cookie
 * consent (the same banner that gates PostHog). Only the "all" choice enables it;
 * "essential" or no decision keeps every track() call a no-op.
 */
export function hasTrackingConsent(): boolean {
  if (!hasWindow()) return false
  return getCookieConsent() === 'all'
}

export interface TrackOpts {
  surface?: CrmInteractionSurface
  sourceKey?: string | null
  path?: string | null
  referrer?: string | null
  email?: string | null
  utm?: Record<string, string>
  metadata?: Record<string, unknown>
}

export interface TrackContext {
  anonymousId: string
  sessionId: string
  consent: boolean
}

/**
 * Pure payload builder (exported for tests). Crucially: `email` is dropped unless
 * consent is granted, so PII can never leak through an un-consented event.
 */
export function buildTrackPayload(
  eventType: CrmInteractionEventType,
  opts: TrackOpts,
  ctx: TrackContext,
): CrmInteractionEventInput {
  const payload: CrmInteractionEventInput = {
    anonymousId: ctx.anonymousId,
    sessionId: ctx.sessionId,
    surface: opts.surface ?? 'platform',
    sourceKey: opts.sourceKey ?? null,
    eventType,
    path: opts.path ?? null,
    referrer: opts.referrer ?? null,
    utm: opts.utm ?? {},
    consent: ctx.consent,
    metadata: opts.metadata ?? {},
  }
  if (ctx.consent && opts.email) payload.email = opts.email
  return payload
}

function parseUtm(search: string): Record<string, string> {
  const out: Record<string, string> = {}
  try {
    const params = new URLSearchParams(search)
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const v = params.get(key)
      if (v) out[key] = v
    }
  } catch {
    /* ignore */
  }
  return out
}

export function track(eventType: CrmInteractionEventType, opts: TrackOpts = {}): void {
  // No network in demo, when the backend isn't configured, or without consent.
  if (!hasWindow() || isDemo() || !isSupabaseConfigured) return
  if (!hasTrackingConsent()) return

  const ctx: TrackContext = {
    anonymousId: getAnonId(),
    sessionId: getSessionId(),
    consent: true,
  }
  const payload = buildTrackPayload(eventType, opts, ctx)
  if (!payload.utm || Object.keys(payload.utm).length === 0) {
    payload.utm = parseUtm(window.location.search)
  }

  const url = `${SUPABASE_URL}/functions/v1/track-interaction`
  const body = JSON.stringify(payload)
  try {
    // Prefer sendBeacon — survives page navigation, never blocks the UI.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      return
    }
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY },
      body,
    }).catch(() => {})
  } catch {
    /* tracking must never throw into the app */
  }
}

export const trackPageView = (path: string, surface: CrmInteractionSurface = 'platform') =>
  track('page_view', { surface, path, sourceKey: path })

export const trackToolRun = (toolKey: string, metadata?: Record<string, unknown>) =>
  track('tool_run', { surface: 'blade', sourceKey: toolKey, metadata })

export const trackLinkClick = (target: string) =>
  track('link_click', { sourceKey: target })

export const trackCta = (key: string, surface: CrmInteractionSurface = 'platform') =>
  track('cta_click', { surface, sourceKey: key })

export const trackLeadCapture = (email: string, sourceKey: string, surface: CrmInteractionSurface = 'blade') =>
  track('lead_capture', { surface, sourceKey, email })
